import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { api, type PageResult } from '@/lib/api'

/**
 * Quests / task groups (spec Requirement 3). Shapes follow `quests`,
 * `quest_tasks` and `quest_task_conditions`.
 *
 * Three rules from the spec drive nearly every decision in this screen:
 *
 * 1. **A published quest is immutable** — the only field that may change after
 *    publish is `ends_at`. A mistake is fixed by ending the quest and publishing
 *    a replacement, never by editing under members who are mid-progress.
 * 2. **`status` moves draft → active only.** There is no un-publishing, because
 *    immutability means going back to draft could not fix anything anyway.
 * 3. **`reward_points` is derived from the sum of task credits while the quest is
 *    a draft, then frozen at publish.** So the console never sends it — the
 *    contract computes it from `SUM(task.internal_credit)` when it is omitted.
 *    This diverges from the prototype, which showed an editable points field
 *    "defaulting to" the sum; an editable copy of a derived number is exactly
 *    how the total drifts away from the tasks it was calculated from.
 */

/**
 * Completion conditions, in the order they appear in the enum table
 * (`quest_task_conditions.type` = 1..5).
 *
 * `kind` is the important column for staff: **only `staff_manual` ever reaches
 * the Rewards stage ① verification queue.** The other four are evaluated by
 * reading `users` / `auth_identities` at request time, so a task using them
 * completes itself with no staff involvement.
 */
export const CONDITION_TYPES = [
  {
    type: 'account_created',
    label: 'Account created',
    kind: 'auto',
    hint: 'True once the member has an account. Reads users.created_at.',
  },
  {
    type: 'profile_completed',
    label: 'Profile completed',
    kind: 'auto',
    hint: 'Definition is still open (spec Q12) — in practice, an avatar has been uploaded.',
  },
  {
    type: 'biconomy_bound',
    label: 'Biconomy UID bound',
    kind: 'auto',
    hint: 'Uniqueness-checked on binding, with no staff verification — so it never reaches the verification queue.',
  },
  {
    type: 'twitter_bound',
    label: 'X (Twitter) handle bound',
    kind: 'auto',
    hint: 'Manually typed by the member, not OAuth — uniqueness-checked only, same as Biconomy. No follow/verification check in Phase 1.',
  },
  {
    type: 'staff_manual',
    label: 'Staff verifies manually',
    kind: 'manual',
    hint: 'Anything a human can check — "Join Discord", "Attend AMA". Appears in the Rewards verification queue.',
  },
  {
    type: 'follow_x',
    label: 'Follow on X',
    kind: 'reserved',
    hint: 'Reserved and rejected by validation in Phase 1 — the integration does not exist yet. Distinct from `twitter_bound`: this would check the member actually follows the account, not just that they typed a handle.',
  },
] as const

/** The five types the backend accepts. `follow_x` is deliberately excluded. */
export const ALLOWED_CONDITIONS = [
  'account_created',
  'profile_completed',
  'biconomy_bound',
  'twitter_bound',
  'staff_manual',
] as const

/**
 * Deliberately narrowed to what may be *authored*, not to everything the label
 * table knows about. `follow_x` has a label because it appears in the enum and a
 * stored row would still need rendering, but it can never be selected — typing
 * it as authorable would let a 1001 through the compiler.
 */
export type ConditionType = (typeof ALLOWED_CONDITIONS)[number]

export const CONDITION_LABEL: Record<string, string> = Object.fromEntries(
  CONDITION_TYPES.map(({ type, label }) => [type, label]),
)

export const COMPLETION_RULES = [
  { code: 'ALL', label: 'All tasks — AND' },
  { code: 'N_OF', label: 'Complete N of M — OR' },
] as const

export const REWARD_RELEASES = [
  { code: 'require_approval', label: 'Require staff approval' },
  { code: 'auto_credit', label: 'Auto-credit on completion' },
] as const

/**
 * A sub-task. `conditions` is an array because the column is a separate table,
 * but Phase 1 allows **exactly one** row per task (`uk_quest_task_conditions_task`
 * is unique on `task_id`), so the form edits a single select and wraps it here.
 */
/**
 * Predicate that caps a string by CODE POINTS, the way the server does.
 *
 * `z.string().max(n)` measures `String.length`, which is UTF-16 code units — an
 * astral-plane character (most emoji, rarer CJK) counts as two. The server uses
 * `utf8.RuneCountInString`, and `varchar(n)` on utf8mb4 is n characters, so the
 * SPA was the only one of the three counting differently: 61 emoji are 122 code
 * units but 61 runes, so the form refused a name the API would have stored.
 *
 * A predicate for `.refine()`, deliberately NOT a schema combined with `.and()`:
 * intersecting it with a `.trim()`ed string type-checks but throws "Unmergable
 * intersection" at runtime the moment trimming makes the two branches disagree,
 * which any trailing space does.
 */
const withinCodePoints = (limit: number) => (value: string) => [...value].length <= limit

const taskSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, 'Every sub-task needs a title.')
    .refine(withinCodePoints(160), 'Sub-task titles are capped at 160 characters.'),
  sort_order: z.number().int().min(0),
  // Only meaningful under N_OF — required tasks sit outside the optional pool.
  // Normalised to false under ALL, where every task is required by definition.
  required: z.boolean(),
  internal_credit: z.coerce
    .number()
    .int('Whole points only.')
    .min(0, 'Credit cannot be negative.')
    .max(1_000_000, 'That credit is implausibly large.'),
  conditions: z
    .array(z.object({ type: z.enum(ALLOWED_CONDITIONS) }))
    .length(1, 'Exactly one condition per sub-task in Phase 1.'),
})

export type TaskInput = z.infer<typeof taskSchema>

export const questSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, 'A quest name is required.')
      .refine(withinCodePoints(120), 'Quest names are capped at 120 characters.'),
    description: z
      .string()
      .refine(withinCodePoints(2000), 'Keep the description under 2000 characters.')
      .optional(),
    status: z.enum(['draft', 'active']),
    reward_release: z.enum(['require_approval', 'auto_credit']),
    completion_rule: z.enum(['ALL', 'N_OF']),
    threshold: z.coerce.number().int('Whole numbers only.').min(1, 'N must be at least 1.').optional(),
    // ISO-8601 UTC on the wire. null is meaningful on both: no scheduled start
    // (live as soon as active) and no expiry.
    starts_at: z.string().nullable(),
    ends_at: z.string().nullable(),
    // .max mirrors the server's maxTasks (app/quest/service/quest_write.go) the
    // same way the length caps above mirror theirs. Without it the form accepts a
    // 51st sub-task and the operator learns the limit from a 400 after saving.
    tasks: z
      .array(taskSchema)
      .min(1, 'A quest needs at least one sub-task.')
      .max(50, 'A quest is capped at 50 sub-tasks.'),
  })
  .superRefine((quest, ctx) => {
    if (quest.completion_rule === 'N_OF') {
      const optional = quest.tasks.filter((task) => !task.required).length
      if (optional === 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['completion_rule'],
          message:
            'Under “N of M” at least one sub-task must be left optional — that is the pool N counts against.',
        })
      } else if (quest.threshold === undefined) {
        ctx.addIssue({ code: 'custom', path: ['threshold'], message: 'N is required under “N of M”.' })
      } else if (quest.threshold > optional) {
        ctx.addIssue({
          code: 'custom',
          path: ['threshold'],
          message: `Only ${optional} sub-task${optional === 1 ? ' is' : 's are'} optional, so N cannot exceed ${optional}.`,
        })
      }
    }

    // A window that ends before it starts can never be live, so the quest would
    // silently do nothing after publish.
    if (quest.starts_at && quest.ends_at && quest.ends_at <= quest.starts_at) {
      ctx.addIssue({ code: 'custom', path: ['ends_at'], message: 'The end must come after the start.' })
    }
  })

export type QuestInput = z.infer<typeof questSchema>

/** `GET /quests/{id}` — the input shape plus the server-owned fields. */
export interface QuestDetail extends QuestInput {
  id: number
  /** Derived while draft, frozen at publish. Read-only in the console. */
  reward_points: number
}

/** A row in the list. */
export interface QuestRow {
  id: number
  name: string
  status: 'draft' | 'active'
  reward_points: number
  reward_release: QuestInput['reward_release']
  completion_rule: QuestInput['completion_rule']
  threshold?: number
  sub_task_count: number
  starts_at: string | null
  ends_at: string | null
}

/**
 * **Live is computed, never stored** — there is no `is_live` column and nothing
 * flips a flag on a schedule. A quest is live when it is active *and* now falls
 * inside its window, so every read re-derives it. Mirrored here so the list
 * badge tells the operator what a member would actually see.
 */
export type QuestLiveState = 'draft' | 'scheduled' | 'live' | 'ended'

export function questLiveState(
  quest: Pick<QuestRow, 'status' | 'starts_at' | 'ends_at'>,
  now: Date = new Date(),
): QuestLiveState {
  if (quest.status === 'draft') return 'draft'
  const iso = now.toISOString()
  if (quest.ends_at && iso > quest.ends_at) return 'ended'
  if (quest.starts_at && iso < quest.starts_at) return 'scheduled'
  return 'live'
}

export const LIVE_LABEL: Record<QuestLiveState, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  live: 'Live',
  ended: 'Ended',
}

export const LIVE_BADGE: Record<QuestLiveState, 'default' | 'warning' | 'success' | 'destructive'> =
  {
    draft: 'default',
    scheduled: 'warning',
    live: 'success',
    ended: 'destructive',
  }

/** The derived reward while a quest is still a draft. */
export function sumCredits(tasks: Pick<TaskInput, 'internal_credit'>[]): number {
  return tasks.reduce((total, task) => total + (Number(task.internal_credit) || 0), 0)
}

// toLocalInput/fromLocalInput/utcLabel moved to @/lib/datetime (promo-banner
// scheduling needs the same UTC<->datetime-local conversions); re-exported
// here so existing imports from this module keep working.
export { toLocalInput, fromLocalInput, utcLabel } from '@/lib/datetime'

const LIST_KEY = 'quests'

export function useQuestList({
  page = 1,
  pageSize = 20,
  status,
}: { page?: number; pageSize?: number; status?: 'draft' | 'active' } = {}) {
  return useQuery({
    queryKey: [LIST_KEY, page, pageSize, status ?? 'all'],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) })
      if (status) params.set('status', status)
      return api.get<PageResult<QuestRow>>(`/admin/quests?${params}`)
    },
  })
}

export function useQuest(id: number | undefined) {
  return useQuery({
    queryKey: [LIST_KEY, 'detail', id],
    queryFn: () => api.get<QuestDetail>(`/admin/quests/${id}`),
    enabled: id !== undefined,
  })
}

/**
 * Create or replace a draft. `reward_points` is deliberately absent from the
 * body so the backend derives it; `sort_order` comes from array position, and
 * `required` is cleared under ALL where it has no meaning.
 */
export function useSaveQuest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id?: number; input: QuestInput }) => {
      const body = normalise(input)
      return id === undefined
        ? api.post<{ id: number }>('/admin/quests', body)
        : api.patch<{ id: number }>(`/admin/quests/${id}`, body)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [LIST_KEY] }),
  })
}

/**
 * The only edit a published quest allows. Kept separate from `useSaveQuest`
 * because sending the whole record back for an active quest is precisely what
 * the immutability rule forbids — the backend rejects it with `2001`.
 */
export function useSetQuestEnd() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ends_at }: { id: number; ends_at: string | null }) =>
      api.patch<{ id: number }>(`/admin/quests/${id}`, { ends_at }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [LIST_KEY] }),
  })
}

/** Form values → request body. */
export function normalise(input: QuestInput): QuestInput {
  const all = input.completion_rule === 'ALL'
  return {
    ...input,
    // `threshold` is required iff N_OF; leaving a stale value on an ALL quest
    // would fail the backend's check constraint.
    threshold: all ? undefined : input.threshold,
    description: input.description?.trim() ? input.description.trim() : undefined,
    tasks: input.tasks.map((task, index) => ({
      ...task,
      sort_order: index,
      required: all ? false : task.required,
    })),
  }
}
